import { useMemo } from 'react'

import * as Prismic from '@prismicio/client'
import {
  type TitleField,
  type RichTextField,
  type FilledImageFieldImage,
  type FilledLinkToWebField,
  type FilledContentRelationshipField,
  asText,
} from '@prismicio/client'
import { type UseQueryResult } from '@tanstack/react-query'

import { usePrismic } from '@dhl/prismic'
import { isProd } from '@dhl/utils'

import { type DashboardItem } from './DashboardItem.js'

let query: string[] = [Prismic.filter.at('document.type', 'dashboard-item')]

if (isProd()) {
  query = [...query, Prismic.filter.not('document.tags', ['accept'])]
}

type PrismicDashboardItem = {
  title: TitleField
  content: RichTextField
  image: FilledImageFieldImage
  link: FilledLinkToWebField
  linked_document: FilledContentRelationshipField
}

export const usePrismicDashboardItems = (url: string, lang = ''): UseQueryResult<DashboardItem[]> => {
  const options: Prismic.QueryParams = useMemo(
    () => ({
      orderings: [{ field: 'document.last_publication_date', direction: 'desc' }],
      lang,
    }),
    [lang]
  )

  return usePrismic<PrismicDashboardItem, DashboardItem[]>(
    {
      endpoint: url,
      query,
      options,
    },
    {
      query: {
        select: (response) =>
          response?.results?.map((result: Prismic.PrismicDocument<PrismicDashboardItem>) => ({
            id: result.id,
            title: asText(result.data.title),
            content: result.data.content,
            // Prismic always used ?auto=compress,format after each image url. We want to disable them
            // in case of gif, so marketing can be use an animated GIF.
            image: { ...result.data.image, url: result.data.image?.url?.replace(/\.gif.*/, '.gif') },
            publicationDate: result.last_publication_date,
            linkUrl: result.data.link?.url || '',
            linkedDocument: result.data.linked_document
              ? {
                  id: result.data.linked_document.id,
                  type: result.data.linked_document.type,
                  slug: result.data.linked_document.slug,
                }
              : undefined,
          })) ?? [],
      },
    }
  )
}
